// Client-side beat analysis: one decode -> { bpm, segments, duration }.
// BPM via onset-envelope autocorrelation; segments via 1s RMS energy structure.
// Heuristic (no model) — reads tempo + energy shape; good for steady hip-hop/trap.

function bpmFromChannel(ch, sr) {
  const maxSamples = Math.min(ch.length, sr * 40);
  const hop = Math.max(1, Math.floor(sr * 0.01));
  const n = Math.floor(maxSamples / hop);
  if (n < 16) return null;
  const env = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const st = i * hop, e = Math.min(st + hop, maxSamples);
    let s = 0; for (let j = st; j < e; j++) { const v = ch[j]; s += v * v; }
    env[i] = Math.sqrt(s / (e - st || 1));
  }
  const on = new Float32Array(n);
  let mean = 0;
  for (let i = 1; i < n; i++) { const d = env[i] - env[i - 1]; on[i] = d > 0 ? d : 0; mean += on[i]; }
  mean /= n;
  for (let i = 0; i < n; i++) on[i] = Math.max(0, on[i] - mean);
  const fps = sr / hop;
  const minLag = Math.floor((fps * 60) / 190), maxLag = Math.ceil((fps * 60) / 60);
  let best = 0, bestLag = minLag;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let sum = 0; for (let i = lag; i < n; i++) sum += on[i] * on[i - lag];
    sum *= 1 + (lag - minLag) * 0.0006;
    if (sum > best) { best = sum; bestLag = lag; }
  }
  if (best <= 0) return null;
  let bpm = (60 * fps) / bestLag;
  while (bpm < 70) bpm *= 2;
  while (bpm > 170) bpm /= 2;
  return Math.round(bpm);
}

function segmentsFromChannel(ch, sr) {
  const win = sr; // 1s windows ≈ seconds
  const n = Math.floor(ch.length / win);
  if (n < 8) return [];
  const e = new Float32Array(n);
  let mx = 0;
  for (let i = 0; i < n; i++) {
    const st = i * win, en = Math.min(st + win, ch.length);
    let s = 0; for (let j = st; j < en; j++) { const v = ch[j]; s += v * v; }
    e[i] = Math.sqrt(s / (en - st || 1));
    if (e[i] > mx) mx = e[i];
  }
  if (mx <= 0) return [];
  const sm = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    let s = 0, c = 0;
    for (let k = -2; k <= 2; k++) { const idx = i + k; if (idx >= 0 && idx < n) { s += e[idx] / mx; c++; } }
    sm[i] = s / c;
  }
  const stateOf = (i) => (sm[i] > 0.62 ? 2 : sm[i] > 0.33 ? 1 : 0);
  const bounds = [0];
  let cur = stateOf(0);
  for (let i = 1; i < n; i++) { const st = stateOf(i); if (st !== cur) { bounds.push(i); cur = st; } }
  bounds.push(n);

  // merge runs shorter than 5s into the previous
  const raw = [];
  for (let b = 0; b < bounds.length - 1; b++) raw.push({ s: bounds[b], e: bounds[b + 1] });
  const merged = [];
  for (const seg of raw) {
    const last = merged[merged.length - 1];
    if (last && (seg.e - seg.s < 5 || last.e - last.s < 5)) last.e = seg.e;
    else merged.push({ ...seg });
  }
  return merged.map((seg, idx) => {
    let s = 0; for (let i = seg.s; i < seg.e; i++) s += sm[i];
    const avg = s / (seg.e - seg.s);
    let label = avg > 0.62 ? 'HOOK' : avg > 0.33 ? 'VERSE' : 'BREAK';
    if (idx === 0 && avg <= 0.5) label = 'INTRO';
    if (idx === merged.length - 1 && avg <= 0.5) label = 'OUTRO';
    return { start: seg.s, end: seg.e, label, energy: Math.round(avg * 100) / 100 };
  });
}

export async function analyzeBeat(arrayBuffer) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return { bpm: null, segments: [], duration: 0 };
  const ctx = new Ctx();
  let buf;
  try { buf = await ctx.decodeAudioData(arrayBuffer.slice(0)); }
  catch { try { ctx.close(); } catch { /* noop */ } return { bpm: null, segments: [], duration: 0 }; }
  try { ctx.close(); } catch { /* noop */ }
  const ch = buf.getChannelData(0);
  return { bpm: bpmFromChannel(ch, buf.sampleRate), segments: segmentsFromChannel(ch, buf.sampleRate), duration: Math.round(buf.duration) };
}

// kept for compatibility
export async function detectBpm(arrayBuffer) { return (await analyzeBeat(arrayBuffer)).bpm; }
